/**
 * DataBest — Read-only tree view of a plan hierarchy.
 * Used inside PlanBuilderPage to visualise Outcomes → Activities → Tasks.
 */
import React, { useState } from "react";
import {
    ChevronDown,
    ChevronRight,
    Target,
    Layers,
    ListChecks,
} from "lucide-react";

const OutcomeNode = ({ outcome }) => {
    const [open, setOpen] = useState(true);
    return (
        <div
            className="rounded-xl border border-brand-blue-100 bg-brand-blue-50/40"
            data-testid="tree-outcome"
        >
            <button
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center gap-3 px-4 py-3 text-left"
            >
                {open ? (
                    <ChevronDown className="h-4 w-4 text-brand-blue-700" />
                ) : (
                    <ChevronRight className="h-4 w-4 text-brand-blue-700" />
                )}
                <Target className="h-4 w-4 text-brand-blue-700" />
                <div className="flex-1">
                    <p className="font-heading font-bold text-brand-blue-950">
                        {outcome.title}
                    </p>
                    <p className="text-xs text-slate-600">
                        {outcome.life_area || "—"}
                        {" · "}
                        {(outcome.activities || []).length} activities
                    </p>
                </div>
            </button>
            {open && (
                <div className="border-t border-brand-blue-100 p-3">
                    {(outcome.activities || []).map((a) => (
                        <ActivityNode key={a.id || a.title} activity={a} />
                    ))}
                </div>
            )}
        </div>
    );
};

const ActivityNode = ({ activity }) => {
    const [open, setOpen] = useState(false);
    return (
        <div
            className="mb-2 rounded-lg border border-slate-200 bg-white"
            data-testid="tree-activity"
        >
            <button
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left"
            >
                {open ? (
                    <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
                ) : (
                    <ChevronRight className="h-3.5 w-3.5 text-slate-500" />
                )}
                <Layers className="h-3.5 w-3.5 text-brand-green-600" />
                <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-800">
                        {activity.title}
                        {activity.skill_building && (
                            <span className="ml-2 rounded-full bg-brand-green-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-green-800">
                                skill-building
                            </span>
                        )}
                    </p>
                    <p className="text-xs text-slate-500">
                        {(activity.tasks || []).length} tasks
                    </p>
                </div>
            </button>
            {open && (activity.tasks || []).length > 0 && (
                <div className="border-t border-slate-100 px-3 py-2">
                    {activity.tasks.map((t, idx) => (
                        <TaskNode key={t.id || idx} task={t} />
                    ))}
                </div>
            )}
        </div>
    );
};

const TaskNode = ({ task }) => (
    <div
        className="mb-1 flex items-start gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-slate-50"
        data-testid="tree-task"
    >
        <ListChecks className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
        <div className="flex-1">
            <p className="font-medium text-slate-800">{task.title}</p>
            {task.support_instructions && (
                <p className="mt-0.5 text-xs text-slate-500">
                    {task.support_instructions.slice(0, 160)}
                    {task.support_instructions.length > 160 ? "…" : ""}
                </p>
            )}
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono">
                    {task.frequency}
                    {task.frequency_value && task.frequency_value !== 1
                        ? ` × ${task.frequency_value}`
                        : ""}
                </span>
                {task.documentation_requirement && (
                    <span className="italic">
                        Doc:{" "}
                        {task.documentation_requirement.slice(0, 80)}
                        {task.documentation_requirement.length > 80 ? "…" : ""}
                    </span>
                )}
            </div>
        </div>
    </div>
);

const PlanTree = ({ outcomes = [] }) => {
    if (!outcomes || outcomes.length === 0) {
        return (
            <div className="rounded-xl border-2 border-dashed border-slate-200 p-10 text-center text-slate-500">
                <Target className="mx-auto h-10 w-10 text-slate-300" />
                <p className="mt-3 font-semibold text-slate-700">
                    No outcomes yet
                </p>
                <p className="mt-1 text-sm">
                    Upload an ISP PDF and let Claude structure the tree, or
                    add outcomes manually.
                </p>
            </div>
        );
    }
    return (
        <div className="space-y-3" data-testid="plan-tree">
            {outcomes.map((o) => (
                <OutcomeNode key={o.id || o.title} outcome={o} />
            ))}
        </div>
    );
};

export default PlanTree;
